import type { ArtPath } from './coins-tile-art';

/**
 * The closed hands that carry the coins. Each finger is redrawn at a new angle about its knuckle, whole: its
 * outline points turn together, so it keeps its drawn shape and only the joint into the palm flexes. A range
 * starts at the control point before its first anchor, so the curve stays smooth where the finger leaves the
 * palm. Indices count the outline's coordinate pairs, in drawing order. Opening is the way back to the drawing.
 */
type Finger = {
	/** Which of the hand's outlines the finger belongs to. */
	path: number;
	from: number;
	to: number;
	knuckle: readonly [number, number];
	/** Degrees, clockwise, from the drawn pose to closed round the coin. */
	closed: number;
	/** A finger bent towards us reads shorter: its length, from the knuckle, once closed. */
	fold?: number;
	/** Drawn outright where turning the finger cannot show it, as with a fist: one point per pair in the range. */
	drawn?: readonly (readonly [number, number])[];
};

/**
 * Back to front, the outlines that overlap as the fingers close; each is filled, so a fingertip tucks behind
 * the finger before it. Left to right, matching `HANDS`.
 */
export const STACK: readonly (readonly number[])[] = [[], [], [3, 2, 1]];

/** Left to right, matching `HANDS`. */
export const FINGERS: readonly (readonly Finger[])[] = [
	[
		{ path: 1, from: 2, to: 8, knuckle: [160, 104], closed: 28 },
		{ path: 1, from: 11, to: 13, knuckle: [164, 113], closed: 30 },
		{ path: 1, from: 17, to: 19, knuckle: [168, 123], closed: 26 },
		{ path: 1, from: 23, to: 25, knuckle: [171, 133], closed: 20 },
	],
	[
		{ path: 7, from: 22, to: 25, knuckle: [207, 210], closed: -16 },
		{ path: 7, from: 13, to: 20, knuckle: [221, 200], closed: -12 },
		{ path: 7, from: 8, to: 11, knuckle: [235, 196], closed: -6 },
		{ path: 7, from: 2, to: 5, knuckle: [247, 200], closed: -4 },
	],
	[
		// The thumb stays put as the pivot; the fingers close in on it round the coin.
		{ path: 1, from: 22, to: 33, knuckle: [279, 133], closed: -6, fold: 0.96 },
		{ path: 2, from: 0, to: 5, knuckle: [306, 134], closed: -11 },
		{ path: 3, from: 0, to: 4, knuckle: [314, 135], closed: -10 },
		// The little finger keeps its drawn shape, only leaning in.
		{ path: 3, from: 8, to: 11, knuckle: [318, 141], closed: -7 },
	],
];

/** The outline with each of its fingers closed; same commands, so SMIL can morph it open to the drawing. */
export function drawClosed(path: ArtPath, index: number, fingers: readonly Finger[]) {
	const mine = fingers.filter((finger) => finger.path === index);
	if (mine.length === 0) return path.d;
	const [ox = 0, oy = 0] = (path.at ?? '0 0').split(' ').map(Number);
	let pair = -1;
	return path.d.replace(/(-?[\d.]+),(-?[\d.]+)/g, (match, x: string, y: string) => {
		pair += 1;
		const finger = mine.find((f) => pair >= f.from && pair <= f.to);
		if (!finger) return match;
		const drawn = finger.drawn?.[pair - finger.from];
		if (drawn) return `${(drawn[0] - ox).toFixed(2)},${(drawn[1] - oy).toFixed(2)}`;
		const angle = (finger.closed * Math.PI) / 180;
		const [kx, ky] = finger.knuckle;
		const rx = Number(x) + ox - kx;
		const ry = Number(y) + oy - ky;
		const fold = finger.fold ?? 1;
		const px = kx + fold * (rx * Math.cos(angle) - ry * Math.sin(angle)) - ox;
		const py = ky + fold * (rx * Math.sin(angle) + ry * Math.cos(angle)) - oy;
		return `${px.toFixed(2)},${py.toFixed(2)}`;
	});
}
